/**
 * SortBar - Attribute sort buttons + A-Z and Company sort, shown in expanded model grid.
 */

import { ArrowUp, ArrowDown, ArrowDownAZ, Building2, DollarSign, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ATTRIBUTE_FIXED_COLORS } from '@/constants/llm/modelAttributes';

// Sort types
export type SortAttribute =
  | 'speed'
  | 'agility'
  | 'intelligence'
  | 'power'
  | 'attack'
  | 'defense'
  | 'name'
  | 'provider'
  | 'cheapest';
export type SortDirection = 'asc' | 'desc';
export type SortState = { attribute: SortAttribute; direction: SortDirection } | null;

/** RPG attribute sort buttons */
const STAT_SORT_BUTTONS: Array<{ key: SortAttribute; abbrev: string }> = [
  { key: 'speed', abbrev: 'SPD' },
  { key: 'agility', abbrev: 'AGI' },
  { key: 'intelligence', abbrev: 'INT' },
  { key: 'power', abbrev: 'PWR' },
  { key: 'attack', abbrev: 'ATK' },
  { key: 'defense', abbrev: 'DEF' },
];

/** Background color variants matching each attribute's identity color */
const ATTRIBUTE_BG_COLORS: Record<string, string> = {
  SPD: 'bg-blue-400/20 border-blue-400/40',
  AGI: 'bg-green-400/20 border-green-400/40',
  INT: 'bg-purple-400/20 border-purple-400/40',
  PWR: 'bg-orange-400/20 border-orange-400/40',
  ATK: 'bg-red-400/20 border-red-400/40',
  DEF: 'bg-cyan-400/20 border-cyan-400/40',
};

export function SortBar({
  sortBy,
  onSortChange,
}: {
  sortBy: SortState;
  onSortChange: (newSort: SortState) => void;
}) {
  const { t } = useTranslation();

  const handleClick = (attr: SortAttribute, defaultDir: SortDirection = 'desc') => {
    if (sortBy?.attribute === attr) {
      onSortChange({ attribute: attr, direction: sortBy.direction === 'desc' ? 'asc' : 'desc' });
    } else {
      onSortChange({ attribute: attr, direction: defaultDir });
    }
  };

  const renderDirectionIcon = (attr: SortAttribute) => {
    if (sortBy?.attribute !== attr) return null;
    return sortBy.direction === 'desc' ? (
      <ArrowDown className="w-2.5 h-2.5" />
    ) : (
      <ArrowUp className="w-2.5 h-2.5" />
    );
  };

  const isNameActive = sortBy?.attribute === 'name';
  const isProviderActive = sortBy?.attribute === 'provider';
  const isCheapestActive = sortBy?.attribute === 'cheapest';

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] text-white/40 mr-0.5 shrink-0">
        {t('llm.sort.label', 'Sort by')}
      </span>

      {/* General sort buttons */}
      <button
        type="button"
        onClick={() => handleClick('cheapest', 'asc')}
        title="Cheapest"
        className={`
          flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold transition-all border
          ${
            isCheapestActive
              ? 'bg-emerald-400/20 border-emerald-400/40 text-emerald-400'
              : 'bg-white/5 border-transparent text-white/40 hover:text-white/60 hover:bg-white/10'
          }
        `}
      >
        <DollarSign className="w-3 h-3" />
        Cheapest
        {renderDirectionIcon('cheapest')}
      </button>
      <button
        type="button"
        onClick={() => handleClick('name', 'asc')}
        title={t('llm.sort.alphabetical', 'Alphabetical')}
        className={`
          flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold transition-all border
          ${
            isNameActive
              ? 'bg-amber-400/20 border-amber-400/40 text-amber-400'
              : 'bg-white/5 border-transparent text-white/40 hover:text-white/60 hover:bg-white/10'
          }
        `}
      >
        <ArrowDownAZ className="w-3 h-3" />
        A-Z
        {renderDirectionIcon('name')}
      </button>
      <button
        type="button"
        onClick={() => handleClick('provider', 'asc')}
        title={t('llm.sort.company', 'Company')}
        className={`
          flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold transition-all border
          ${
            isProviderActive
              ? 'bg-pink-400/20 border-pink-400/40 text-pink-400'
              : 'bg-white/5 border-transparent text-white/40 hover:text-white/60 hover:bg-white/10'
          }
        `}
      >
        <Building2 className="w-3 h-3" />
        {t('llm.sort.company', 'Company')}
        {renderDirectionIcon('provider')}
      </button>

      {/* Separator */}
      <div className="w-px h-3.5 bg-white/10 mx-0.5" />

      {/* RPG attribute sort buttons */}
      {STAT_SORT_BUTTONS.map(({ key, abbrev }) => {
        const isActive = sortBy?.attribute === key;
        const textColor = ATTRIBUTE_FIXED_COLORS[abbrev] || 'text-white/50';
        const activeBg = ATTRIBUTE_BG_COLORS[abbrev] || 'bg-white/10 border-white/20';
        return (
          <button
            key={key}
            type="button"
            onClick={() => handleClick(key)}
            title={
              isActive
                ? sortBy.direction === 'desc'
                  ? t('llm.sort.desc', 'Highest first')
                  : t('llm.sort.asc', 'Lowest first')
                : abbrev
            }
            className={`
              flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-semibold transition-all border
              ${
                isActive
                  ? `${activeBg} ${textColor}`
                  : `bg-white/5 border-transparent text-white/40 hover:text-white/60 hover:bg-white/10`
              }
            `}
          >
            {abbrev}
            {renderDirectionIcon(key)}
          </button>
        );
      })}

      {/* Clear button */}
      {sortBy && (
        <button
          type="button"
          onClick={() => onSortChange(null)}
          title={t('llm.sort.clear', 'Clear sort')}
          className="p-0.5 rounded text-white/30 hover:text-white/60 hover:bg-white/10 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default SortBar;
